import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, query, orderBy, where, limit } from 'firebase/firestore';
import { STOCKS } from '../constants/stocks';

let _stockStatsCache = null;

function StockStatsContent() {
  const [stats, setStats] = useState(_stockStatsCache);
  const [loading, setLoading] = useState(!_stockStatsCache);
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    if (_stockStatsCache) return;
    loadStockStats();
  }, []);

  async function loadStockStats() {
    setLoading(true);
    try {
      // Load users for name resolution
      const usersSnap = await getDocs(collection(db, 'users'));
      const uMap = {};
      usersSnap.forEach(d => {
        const data = d.data();
        if (data.email !== 'test@test.com') {
          uMap[d.id] = data.ign || data.email?.split('@')[0] || d.id.slice(0, 8);
        }
      });
      setUserMap(uMap);

      // Load all holdings + current prices
      const [holdingsSnap, pricesSnap] = await Promise.all([
        getDocs(collection(db, 'stockHoldings')),
        getDoc(doc(db, 'stockPrices', 'current')),
      ]);
      const allHoldings = [];
      holdingsSnap.forEach(d => {
        allHoldings.push({ userId: d.id, ...d.data() });
      });
      const prices = pricesSnap.exists() ? pricesSnap.data().prices || {} : {};

      // Load recent transactions (last 200) for activity stats
      const txRef = collection(db, 'stockTransactions');
      const [txSnap, gainsSnap, lossesSnap] = await Promise.all([
        getDocs(query(txRef, orderBy('timestamp', 'desc'), limit(200))),
        // Targeted queries for biggest gains/losses across ALL transactions
        getDocs(query(txRef, where('type', '==', 'sell'), orderBy('profit', 'desc'), limit(5))),
        getDocs(query(txRef, where('type', '==', 'sell'), orderBy('profit', 'asc'), limit(5))),
      ]);
      const transactions = txSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Helper: get current price for a stock id
      function getCurrentPrice(stockId) {
        const stock = STOCKS.find(s => s.id === stockId);
        if (!stock) return 0;
        return prices[stock.ticker]?.price || 0;
      }

      // Calculate live portfolio values and P&L per user
      const userPnL = [];
      allHoldings.forEach(h => {
        const holdings = h.holdings || {};
        let totalValue = 0;
        let totalCostBasis = 0;
        for (const [stockId, holding] of Object.entries(holdings)) {
          if (holding.shares > 0) {
            const price = getCurrentPrice(stockId);
            totalValue += holding.shares * price;
            totalCostBasis += holding.costBasis || 0;
          }
        }
        if (totalValue > 0 || totalCostBasis > 0) {
          userPnL.push({
            userId: h.userId,
            portfolioValue: totalValue,
            costBasis: totalCostBasis,
            pnl: totalValue - totalCostBasis,
            pnlPercent: totalCostBasis > 0 ? ((totalValue - totalCostBasis) / totalCostBasis * 100) : 0,
          });
        }
      });

      // Top portfolios by live value
      const topPortfolios = userPnL
        .filter(u => u.portfolioValue > 0)
        .sort((a, b) => b.portfolioValue - a.portfolioValue)
        .slice(0, 10);

      // Biggest winners (highest unrealized P&L)
      const biggestWinners = userPnL
        .filter(u => u.pnl > 0)
        .sort((a, b) => b.pnl - a.pnl)
        .slice(0, 10);

      // Biggest losers (lowest unrealized P&L)
      const biggestLosers = userPnL
        .filter(u => u.pnl < 0)
        .sort((a, b) => a.pnl - b.pnl)
        .slice(0, 10);

      // Most active traders
      const traderCounts = {};
      transactions.forEach(tx => {
        traderCounts[tx.userId] = (traderCounts[tx.userId] || 0) + 1;
      });
      const mostActive = Object.entries(traderCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      // Biggest single-trade gains (from targeted query across ALL sells)
      const biggestGains = gainsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(tx => tx.profit > 0);

      // Biggest single-trade losses (from targeted query across ALL sells)
      const biggestLosses = lossesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(tx => tx.profit < 0);

      // Most popular stocks (by total dollar value invested)
      const stockValue = {};
      allHoldings.forEach(h => {
        const holdings = h.holdings || {};
        for (const [stockId, holding] of Object.entries(holdings)) {
          if (holding.shares > 0) {
            const price = getCurrentPrice(stockId);
            const value = holding.shares * price;
            stockValue[stockId] = (stockValue[stockId] || 0) + value;
          }
        }
      });
      const popularStocks = Object.entries(stockValue)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      const result = { topPortfolios, mostActive, biggestGains, biggestLosses, popularStocks, biggestWinners, biggestLosers };
      _stockStatsCache = result;
      setStats(result);
    } catch (err) {
      console.error('Failed to load stock stats:', err);
    } finally {
      setLoading(false);
    }
  }

  function getUserName(userId) {
    return userMap[userId] || userId.slice(0, 8);
  }

  function getStockName(stockId) {
    const stock = STOCKS.find(s => s.id === stockId);
    return stock ? `${stock.icon} ${stock.name}` : stockId;
  }

  if (loading) {
    return <div className="stock-stats-container"><p>Loading stock stats...</p></div>;
  }

  if (!stats) {
    return <div className="stock-stats-container"><p>No stock data available yet.</p></div>;
  }

  return (
    <div className="stock-stats-container">
      <div className="stock-stats-grid">
        {/* Top Portfolios */}
        <div className="stock-stats-card">
          <h4>Top Portfolios</h4>
          {stats.topPortfolios.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No portfolios yet</p>}
          {stats.topPortfolios.map((h, i) => (
            <div key={h.userId} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                #{i + 1} {getUserName(h.userId)}
              </span>
              <span className="stock-stats-entry-value">
                {(h.portfolioValue || 0).toFixed(2)} 🔵
              </span>
            </div>
          ))}
        </div>

        {/* Most Active Traders */}
        <div className="stock-stats-card">
          <h4>Most Active Traders</h4>
          {stats.mostActive.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No trades yet</p>}
          {stats.mostActive.map(([userId, count], i) => (
            <div key={userId} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                #{i + 1} {getUserName(userId)}
              </span>
              <span className="stock-stats-entry-value">{count} trades</span>
            </div>
          ))}
        </div>

        {/* Biggest Gains */}
        <div className="stock-stats-card">
          <h4>Biggest Single-Trade Gains</h4>
          {stats.biggestGains.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No profitable trades yet</p>}
          {stats.biggestGains.map((tx, i) => (
            <div key={tx.id} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                {getUserName(tx.userId)} — {getStockName(tx.stockId)}
              </span>
              <span className="stock-stats-entry-value positive">
                +{tx.profit.toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        {/* Biggest Losses */}
        <div className="stock-stats-card">
          <h4>Biggest Single-Trade Losses</h4>
          {stats.biggestLosses.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No losing trades yet</p>}
          {stats.biggestLosses.map((tx, i) => (
            <div key={tx.id} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                {getUserName(tx.userId)} — {getStockName(tx.stockId)}
              </span>
              <span className="stock-stats-entry-value negative">
                {tx.profit.toFixed(2)}
              </span>
            </div>
          ))}
        </div>

        {/* Most Popular Stocks */}
        <div className="stock-stats-card">
          <h4>Most Popular Stocks</h4>
          {stats.popularStocks.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No stocks held yet</p>}
          {stats.popularStocks.map(([stockId, value], i) => (
            <div key={stockId} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                #{i + 1} {getStockName(stockId)}
              </span>
              <span className="stock-stats-entry-value">${value.toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Biggest Winners */}
        <div className="stock-stats-card">
          <h4>Up The Most</h4>
          {stats.biggestWinners.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No one is up yet</p>}
          {stats.biggestWinners.map((u, i) => (
            <div key={u.userId} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                #{i + 1} {getUserName(u.userId)}
              </span>
              <span className="stock-stats-entry-value positive">
                +${u.pnl.toFixed(2)} ({u.pnlPercent.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>

        {/* Biggest Losers */}
        <div className="stock-stats-card">
          <h4>Down The Most</h4>
          {stats.biggestLosers.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No one is down yet</p>}
          {stats.biggestLosers.map((u, i) => (
            <div key={u.userId} className="stock-stats-entry">
              <span className="stock-stats-entry-name">
                #{i + 1} {getUserName(u.userId)}
              </span>
              <span className="stock-stats-entry-value negative">
                -${Math.abs(u.pnl).toFixed(2)} ({u.pnlPercent.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default StockStatsContent;
